import { Alert, Box, Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useValidateDeliveryGnrRequestMutation } from '../../../../hooks/mutations/useGnrRequestMutation';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useTheme } from '../../../../hooks/useTheme';
import { GnrRequest } from '../../../../services/apiSLTP/sites/gnrRequests';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import CustomDateInput from '../../../ui/CustomDateInput';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import Icon from '../../../ui/Icon';
import IconButton from '../../../ui/IconButton';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ValidateDeliveryGnrRequestModalProps extends ModalProps {
    gnrRequest: GnrRequest | null;
    refetch: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];

const ValidateDeliveryGnrRequestModal: React.FC<ValidateDeliveryGnrRequestModalProps> = ({ gnrRequest, open, handleClose, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme } = useTheme();

    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });

    const [selectedReceiver, setSelectedReceiver] = useState<OptionType | null>(null);
    const [deliveryDate, setDeliveryDate] = useState<Date>(new Date());
    const [receivedVolume, setReceivedVolume] = useState<string>(gnrRequest?.plannedVolume?.toString() || '');
    const [receivedObservations, setReceivedObservations] = useState<string>('');
    const [deliveryReceiptPhoto, setDeliveryReceiptPhoto] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string>('');
    const [isDragging, setIsDragging] = useState(false);

    const { mutate: validateDelivery, isLoading: isValidating } = useValidateDeliveryGnrRequestMutation({
        onSuccess: () => {
            handleClose();
            refetch();
        },
        onError: (error) => console.error('Error validating delivery:', error.message),
    });

    const validateFile = (file: File): string | null => {
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            return t('invalidFileType');
        }
        if (file.size > MAX_FILE_SIZE) {
            return t('fileTooLarge', { maxSize: '10 MB' });
        }
        return null;
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const error = validateFile(file);
            if (error) {
                setFileError(error);
                setDeliveryReceiptPhoto(null);
            } else {
                setFileError('');
                setDeliveryReceiptPhoto(file);
            }
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);

        const file = event.dataTransfer.files[0];
        if (file) {
            const error = validateFile(file);
            if (error) {
                setFileError(error);
                setDeliveryReceiptPhoto(null);
            } else {
                setFileError('');
                setDeliveryReceiptPhoto(file);
            }
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleRemoveFile = () => {
        setDeliveryReceiptPhoto(null);
        setFileError('');
    };

    const handleSubmit = () => {
        if (!gnrRequest || !selectedReceiver || !receivedVolume) return;

        validateDelivery({
            gnrRequestId: gnrRequest._id,
            body: {
                deliveryDate,
                receiverId: selectedReceiver.value,
                receivedVolume: parseFloat(receivedVolume.replace(',', '.')),
                receivedObservations: receivedObservations || undefined,
            },
            file: deliveryReceiptPhoto || undefined,
        });
    };

    useEffect(() => {
        if (gnrRequest && dataUsers) {
            if (!selectedReceiver && user) {
                const currentUser = dataUsers.find((u) => u._id === user._id);
                if (currentUser) {
                    setSelectedReceiver({
                        value: currentUser._id,
                        label: formatName(currentUser.firstName, currentUser.lastName),
                    });
                }
            }

            if (gnrRequest.plannedVolume && !receivedVolume) {
                setReceivedVolume(gnrRequest.plannedVolume.toString());
            }
        }
    }, [gnrRequest, dataUsers, user]);

    return (
        <ModalBody
            title={t('deliveryValidation')}
            subtitle={gnrRequest?.siteId?.name || ''}
            open={open}
            handleClose={handleClose}
            disableSubmit={!(selectedReceiver && receivedVolume && parseFloat(receivedVolume) > 0) || isValidating}
            onSubmit={handleSubmit}
        >
            <>
                <CustomDateInput
                    label={t('deliveryDate')}
                    onChange={(value) => (value ? setDeliveryDate(new Date(value.toISOString())) : setDeliveryDate(new Date()))}
                    required
                    maxDate={new Date()}
                    defaultDate={deliveryDate}
                />

                <DropDown
                    selectedItem={selectedReceiver}
                    items={
                        dataUsers
                            ?.filter((user) => !user.isSpecial)
                            .map((user) => ({
                                label: formatName(user.firstName, user.lastName),
                                value: user._id,
                            })) || []
                    }
                    handleChange={(event, value) => setSelectedReceiver(value)}
                    inputLabel={t('receivedBy')}
                    loading={isUsersLoading}
                    required
                />

                <CustomTextInput
                    value={receivedVolume}
                    label={t('receivedVolume')}
                    onChange={(text) => setReceivedVolume(text)}
                    errorText={t('error')}
                    type="number"
                    required
                />

                <CustomTextInput
                    value={receivedObservations}
                    label={t('observations')}
                    onChange={(text) => setReceivedObservations(text)}
                    multiline
                />

                <Box sx={{ m: 1 }}>
                    <Typography variant="caption" color="textDisabled" sx={{ ml: 2, fontWeight: 500 }}>
                        {capitalizeFirstLetter(t('deliveryReceiptPhoto'))}
                    </Typography>

                    {fileError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {fileError}
                        </Alert>
                    )}

                    {!deliveryReceiptPhoto ? (
                        <Box
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            sx={{
                                border: '2px dashed',
                                borderColor: isDragging ? muiTheme.palette.text.primary : muiTheme.palette.text.disabled,
                                borderRadius: 1,
                                p: 1,
                                textAlign: 'center',
                                backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Icon name="cloudUpload" sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />

                            <Button component="label" variant="contained">
                                {t('addPhoto')}
                                <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic" hidden onChange={handleFileChange} />
                            </Button>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'grey.600' }}>
                                {capitalizeFirstLetter(t('acceptedFormats'))}: JPG, PNG, WEBP, HEIC (max 10 MB)
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                border: '1px solid',
                                borderColor: 'primary.main',
                                borderRadius: 1,
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Icon name="insertPhoto" color="primary" />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {deliveryReceiptPhoto.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                        {(deliveryReceiptPhoto.size / 1024 / 1024).toFixed(2)} MB
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton iconName="close" size="small" onClick={handleRemoveFile} color="error" />
                        </Box>
                    )}
                </Box>
            </>
        </ModalBody>
    );
};

export default ValidateDeliveryGnrRequestModal;
